import React, {useEffect, useRef} from 'react';
import styles from './Toast.module.css';
import {CheckIcon} from '../marketing/icons/Icons';

/**
 * A single dismissable toast: a message, an optional Undo-style action, and
 * a dismiss button. Disappears after `duration` (default 6s), pausing the
 * countdown while hovered or focused.
 */
const Toast = ({message, actionLabel, onAction, onDismiss, duration = 6000}) => {
    const timerRef = useRef(null);
    const remainingRef = useRef(duration);
    const startedAtRef = useRef(0);

    const clear = () => {
        if (timerRef.current) clearTimeout(timerRef.current);
    };
    const start = (ms) => {
        startedAtRef.current = Date.now();
        timerRef.current = setTimeout(onDismiss, ms);
    };

    useEffect(() => {
        start(remainingRef.current);
        return clear;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const pause = () => {
        clear();
        remainingRef.current = Math.max(300, remainingRef.current - (Date.now() - startedAtRef.current));
    };
    const resume = () => start(remainingRef.current);

    return (
        <div
            className={styles.toast}
            role="status"
            onMouseEnter={pause}
            onMouseLeave={resume}
            onFocus={pause}
            onBlur={resume}
        >
            <span className={styles.message}>
                <CheckIcon className={styles.tick}/>
                {message}
            </span>
            {actionLabel && (
                <button type="button" className={styles.action} onClick={onAction}>{actionLabel}</button>
            )}
            <button type="button" aria-label="Dismiss" className={styles.dismiss} onClick={onDismiss}>
                &times;
            </button>
        </div>
    );
};

export default Toast;
